import { useState, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import { BarChart3, TrendingUp, TrendingDown, Percent, Receipt, Download, Loader2 } from 'lucide-react';
import {
  GET_REPORTE_BALANCE_INGRESOS_EGRESOS,
  GET_DETALLE_INGRESOS_EXCEL,
  GET_DETALLE_EGRESOS_EXCEL,
  GET_DETALLE_CUENTAS_COBRO_EXCEL
} from '../../graphql/reportes';
import { exportarDetalleIngresos, exportarDetalleEgresos, exportarDetalleCuentasCobro } from '../../utils/exportarReporte';
import FiltrosReporte from './FiltrosReporte';
import Loading from '../shared/Loading';
import Button from '../shared/Button';
import './ReporteBalanceIngresosEgresos.css';

function ReporteBalanceIngresosEgresos() {
  const [datos, setDatos] = useState(null);
  const fechasRef = useRef({ fechaDesde: null, fechaHasta: null });
  const [exportando, setExportando] = useState({ ingresos: false, egresos: false, cuentas: false });

  const [getReporte, { loading, error }] = useLazyQuery(GET_REPORTE_BALANCE_INGRESOS_EGRESOS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => setDatos(data.reporteBalanceIngresosEgresos),
    onError: (err) => console.error('Error al obtener Balance:', err)
  });

  const [getDetalleIngresos] = useLazyQuery(GET_DETALLE_INGRESOS_EXCEL, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      exportarDetalleIngresos(data.detalleIngresosExcel, fechasRef.current.fechaDesde, fechasRef.current.fechaHasta);
      setExportando(prev => ({ ...prev, ingresos: false }));
    },
    onError: (err) => {
      console.error('Error al exportar ingresos:', err);
      setExportando(prev => ({ ...prev, ingresos: false }));
    }
  });

  const [getDetalleEgresos] = useLazyQuery(GET_DETALLE_EGRESOS_EXCEL, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      exportarDetalleEgresos(data.detalleEgresosExcel, fechasRef.current.fechaDesde, fechasRef.current.fechaHasta);
      setExportando(prev => ({ ...prev, egresos: false }));
    },
    onError: (err) => {
      console.error('Error al exportar egresos:', err);
      setExportando(prev => ({ ...prev, egresos: false }));
    }
  });

  const [getDetalleCuentas] = useLazyQuery(GET_DETALLE_CUENTAS_COBRO_EXCEL, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      exportarDetalleCuentasCobro(data.detalleCuentasCobroPendientesExcel);
      setExportando(prev => ({ ...prev, cuentas: false }));
    },
    onError: (err) => {
      console.error('Error al exportar cuentas de cobro:', err);
      setExportando(prev => ({ ...prev, cuentas: false }));
    }
  });

  const handleFiltrar = ({ fechaDesde, fechaHasta }) => {
    fechasRef.current = { fechaDesde, fechaHasta };
    getReporte({ variables: { fechaDesde, fechaHasta } });
  };

  const handleDescargarIngresos = (e) => {
    e.stopPropagation();
    if (!fechasRef.current.fechaDesde || exportando.ingresos) return;
    setExportando(prev => ({ ...prev, ingresos: true }));
    getDetalleIngresos({ variables: { fechaDesde: fechasRef.current.fechaDesde, fechaHasta: fechasRef.current.fechaHasta } });
  };

  const handleDescargarEgresos = (e) => {
    e.stopPropagation();
    if (!fechasRef.current.fechaDesde || exportando.egresos) return;
    setExportando(prev => ({ ...prev, egresos: true }));
    getDetalleEgresos({ variables: { fechaDesde: fechasRef.current.fechaDesde, fechaHasta: fechasRef.current.fechaHasta } });
  };

  const handleDescargarCuentas = (e) => {
    e.stopPropagation();
    if (exportando.cuentas) return;
    setExportando(prev => ({ ...prev, cuentas: true }));
    getDetalleCuentas();
  };

  const formatearMoneda = (valor) =>
    new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(valor || 0);

  const formatearPorcentaje = (valor) => `${parseFloat(valor || 0).toFixed(2)}%`;

  const formatMes = (mes) => {
    const [anio, m] = mes.split('-');
    const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return `${meses[parseInt(m) - 1]} ${anio}`;
  };

  return (
    <div className="reporte-balance">
      <FiltrosReporte onFiltrar={handleFiltrar} loading={loading} />

      {loading && <Loading />}
      {error && <div className="error-message">Error: {error.message}</div>}

      {datos && (
        <>
          {/* Cards resumen */}
          <div className="reporte-cards">
            <div className="reporte-card reporte-card--clickable" onClick={handleDescargarIngresos} title="Descargar detalle de ingresos en Excel">
              <div className="reporte-card__icon reporte-card__icon--success">
                <TrendingUp size={24} />
              </div>
              <div className="reporte-card__content">
                <div className="reporte-card__label">Total Ingresos</div>
                <div className="reporte-card__value">{formatearMoneda(datos.total_ingresos)}</div>
                <div className="reporte-card__extra">Prom. mensual: {formatearMoneda(datos.promedio_mensual_ingresos)}</div>
              </div>
              <div className="reporte-card__download">
                {exportando.ingresos ? <Loader2 size={16} className="spin" /> : <Download size={16} />}
              </div>
            </div>
            <div className="reporte-card reporte-card--clickable" onClick={handleDescargarEgresos} title="Descargar detalle de egresos en Excel">
              <div className="reporte-card__icon reporte-card__icon--danger">
                <TrendingDown size={24} />
              </div>
              <div className="reporte-card__content">
                <div className="reporte-card__label">Total Egresos</div>
                <div className="reporte-card__value">{formatearMoneda(datos.total_egresos)}</div>
                <div className="reporte-card__extra">Prom. mensual: {formatearMoneda(datos.promedio_mensual_egresos)}</div>
              </div>
              <div className="reporte-card__download">
                {exportando.egresos ? <Loader2 size={16} className="spin" /> : <Download size={16} />}
              </div>
            </div>
            <div className="reporte-card">
              <div className={`reporte-card__icon reporte-card__icon--${datos.resultado_periodo >= 0 ? 'success' : 'danger'}`}>
                <BarChart3 size={24} />
              </div>
              <div className="reporte-card__content">
                <div className="reporte-card__label">Resultado del Período</div>
                <div className={`reporte-card__value ${datos.resultado_periodo < 0 ? 'text-danger' : 'text-success'}`}>
                  {formatearMoneda(datos.resultado_periodo)}
                </div>
              </div>
            </div>
            <div className="reporte-card">
              <div className="reporte-card__icon reporte-card__icon--info">
                <Percent size={24} />
              </div>
              <div className="reporte-card__content">
                <div className="reporte-card__label">Margen del Período</div>
                <div className="reporte-card__value">{formatearPorcentaje(datos.margen_periodo)}</div>
              </div>
            </div>
            {datos.cuentas_cobro_pendientes != null && datos.cuentas_cobro_pendientes > 0 && (
              <div className="reporte-card reporte-card--clickable" onClick={handleDescargarCuentas} title="Descargar detalle de cuentas de cobro en Excel">
                <div className="reporte-card__icon reporte-card__icon--warning">
                  <Receipt size={24} />
                </div>
                <div className="reporte-card__content">
                  <div className="reporte-card__label">Cuentas de Cobro Pendientes</div>
                  <div className="reporte-card__value text-warning">{formatearMoneda(datos.cuentas_cobro_pendientes)}</div>
                </div>
                <div className="reporte-card__download">
                  {exportando.cuentas ? <Loader2 size={16} className="spin" /> : <Download size={16} />}
                </div>
              </div>
            )}
          </div>

          {/* Comparativa mensual */}
          <div className="reporte-seccion">
            <h3 className="reporte-seccion__titulo">Comparativa Mensual</h3>
            <div className="tabla-scroll">
              <table className="reporte-tabla">
                <thead>
                  <tr>
                    <th>Mes</th>
                    <th>Ingresos</th>
                    <th>Egresos</th>
                    <th>Resultado</th>
                    <th>Margen</th>
                    <th># Facturas</th>
                    <th># Gastos</th>
                    <th># Compras</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.balance_mensual.map(mes => (
                    <tr key={mes.mes}>
                      <td><strong>{formatMes(mes.mes)}</strong></td>
                      <td className="text-success">{formatearMoneda(mes.ingresos)}</td>
                      <td className="text-danger">{formatearMoneda(mes.egresos)}</td>
                      <td className={mes.resultado >= 0 ? 'text-success' : 'text-danger'}>
                        <strong>{formatearMoneda(mes.resultado)}</strong>
                      </td>
                      <td>{formatearPorcentaje(mes.margen)}</td>
                      <td>{mes.num_facturas}</td>
                      <td>{mes.num_gastos}</td>
                      <td>{mes.num_compras}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Categorías */}
          <div className="reporte-grid-2">
            <div className="reporte-seccion">
              <h3 className="reporte-seccion__titulo">Ingresos por Categoría</h3>
              <div className="tabla-scroll">
                <table className="reporte-tabla">
                  <thead>
                    <tr>
                      <th>Categoría</th>
                      <th>Total</th>
                      <th>%</th>
                      <th># Registros</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.ingresos_por_categoria.map(cat => (
                      <tr key={cat.categoria}>
                        <td><strong>{cat.categoria}</strong></td>
                        <td>{formatearMoneda(cat.total)}</td>
                        <td>{formatearPorcentaje(cat.porcentaje)}</td>
                        <td>{cat.num_registros}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="reporte-seccion">
              <h3 className="reporte-seccion__titulo">Egresos por Categoría</h3>
              <div className="tabla-scroll">
                <table className="reporte-tabla">
                  <thead>
                    <tr>
                      <th>Categoría</th>
                      <th>Total</th>
                      <th>%</th>
                      <th># Registros</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.egresos_por_categoria.map(cat => (
                      <tr key={cat.categoria}>
                        <td>{cat.categoria}</td>
                        <td>{formatearMoneda(cat.total)}</td>
                        <td>{formatearPorcentaje(cat.porcentaje)}</td>
                        <td>{cat.num_registros}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {!datos && !loading && (
        <div className="reporte-placeholder">
          <BarChart3 size={64} />
          <p>Selecciona un rango de fechas para generar el Balance de Ingresos vs Egresos</p>
        </div>
      )}
    </div>
  );
}

export default ReporteBalanceIngresosEgresos;
